import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, TrendingUp, Users, Scale, ShieldCheck, DollarSign,
  Megaphone, Building2, Briefcase, UserCog, Wallet, FileText, HelpCircle,
  Lightbulb, BarChart3, FileSearch, Receipt, Package, GraduationCap,
  LogOut, Menu, X, KeyRound, Sparkles, ClipboardList
} from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  condition?: boolean;
  startsWith?: boolean;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { profile, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const isActive = (path: string, startsWith?: boolean) => {
    if (startsWith) {
      return location.pathname === path || location.pathname.startsWith(path + '/');
    }
    return location.pathname === path;
  };

  const navItems: NavItem[] = [
    { path: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard className="w-4 h-4" /> },
    { path: '/team-workspace', label: 'Team Workspace', icon: <Users className="w-4 h-4" />, startsWith: true },
    {
      path: '/sales',
      label: 'Sales',
      icon: <TrendingUp className="w-4 h-4" />,
      condition: profile?.canAccessSales || profile?.role === 'operations_admin' || profile?.role === 'executive'
    },
    { path: '/pilot-crm', label: 'Pilot CRM', icon: <ClipboardList className="w-4 h-4" />, startsWith: true },
    {
      path: '/hr',
      label: 'HR',
      icon: <Users className="w-4 h-4" />,
      condition: profile?.canAccessHR || profile?.role === 'operations_admin' || profile?.role === 'executive'
    },
    {
      path: '/legal',
      label: 'Legal',
      icon: <Scale className="w-4 h-4" />,
      condition: profile?.canAccessLegal || profile?.role === 'operations_admin' || profile?.role === 'executive'
    },
    {
      path: '/compliance',
      label: 'Compliance',
      icon: <ShieldCheck className="w-4 h-4" />,
      condition: profile?.canAccessCompliance || profile?.role === 'operations_admin' || profile?.role === 'executive'
    },
    {
      path: '/payroll',
      label: 'Payroll',
      icon: <DollarSign className="w-4 h-4" />,
      condition: profile?.canAccessHR || profile?.role === 'operations_admin' || profile?.role === 'executive'
    },
    {
      path: '/marketing',
      label: 'Marketing',
      icon: <Megaphone className="w-4 h-4" />,
      condition: profile?.canAccessSales || profile?.role === 'operations_admin' || profile?.role === 'executive'
    },
    { path: '/providers', label: 'Providers', icon: <Building2 className="w-4 h-4" />, startsWith: true },
    { path: '/employers', label: 'Employers', icon: <Briefcase className="w-4 h-4" />, startsWith: true },
    { path: '/users', label: 'Users', icon: <UserCog className="w-4 h-4" />, startsWith: true },
    { path: '/financial', label: 'Financial', icon: <Wallet className="w-4 h-4" />, startsWith: true },
    { path: '/expenses', label: 'Expenses', icon: <Receipt className="w-4 h-4" />, startsWith: true },
    { path: '/documents', label: 'Documents', icon: <FileText className="w-4 h-4" />, startsWith: true },
    { path: '/business-documents', label: 'Business Docs', icon: <Building2 className="w-4 h-4" />, startsWith: true },
    { path: '/helpdesk', label: 'Helpdesk', icon: <HelpCircle className="w-4 h-4" />, startsWith: true },
    { path: '/feature-requests', label: 'Features', icon: <Lightbulb className="w-4 h-4" />, startsWith: true },
    { path: '/analytics', label: 'Analytics', icon: <BarChart3 className="w-4 h-4" /> },
    { path: '/audit-logs', label: 'Audit Logs', icon: <FileSearch className="w-4 h-4" /> },
    {
      path: '/password-vault',
      label: 'Password Vault',
      icon: <KeyRound className="w-4 h-4" />,
      condition:
        profile?.role === 'operations_admin' ||
        profile?.role === 'executive' ||
        profile?.role === 'hr' ||
        profile?.role === 'hr_manager' ||
        profile?.role === 'finance',
    },
    // ... (no changes needed for imports)

    { path: '/receipt-review', label: 'Receipt Review', icon: <Receipt className="w-4 h-4" /> },
    { path: '/enrollment', label: 'Enrollment', icon: <Users className="w-4 h-4" /> },
    { path: '/vendors', label: 'Vendors', icon: <Package className="w-4 h-4" />, startsWith: true },
    { path: '/training', label: 'Training', icon: <GraduationCap className="w-4 h-4" />, startsWith: true },
    {
      path: '/seed-training-documents',
      label: 'Seed Data',
      icon: <Package className="w-4 h-4" />,
      condition: profile?.role === 'operations_admin' || profile?.role === 'executive'
    },
    {
      path: '/admin/early-access',
      label: 'Founding Pilot',
      icon: <Sparkles className="w-4 h-4" />
    },
    { path: '/contracts', label: 'Contracts', icon: <FileText className="w-4 h-4" />, startsWith: true },
  ];

  const visibleNavItems = navItems.filter(item => item.condition !== false);

  const NavLink = ({ item }: { item: NavItem }) => {
    const active = isActive(item.path, item.startsWith);
    return (
      <Link
        to={item.path}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${active
            ? 'bg-blue-50 text-blue-700 border border-blue-200'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
        onClick={() => setMobileMenuOpen(false)}
      >
        {item.icon}
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2 text-xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent hover:opacity-80 transition-opacity">
                <ShieldCheck className="w-6 h-6 text-blue-600" />
                <span className="hidden sm:inline">FairCare Operations</span>
                <span className="sm:hidden">FairCare</span>
              </Link>
              <div className="hidden lg:flex lg:ml-8 lg:gap-1 lg:flex-wrap lg:items-center lg:max-w-5xl">
                {visibleNavItems.map((item) => (
                  <NavLink key={item.path} item={item} />
                ))}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden flex items-center gap-4">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>

            {/* Desktop user menu */}
            <div className="hidden lg:flex items-center gap-4">
              <div className="text-sm font-medium text-gray-700">
                {profile?.fullName}
              </div>
              <button
                onClick={handleSignOut}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <div className="px-4 pt-2 pb-4 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto">
              {visibleNavItems.map((item) => (
                <NavLink key={item.path} item={item} />
              ))}
              <div className="pt-4 mt-4 border-t border-gray-200">
                <div className="px-3 py-2 text-sm font-medium text-gray-700">
                  {profile?.fullName}
                </div>
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="min-h-[calc(100vh-4rem)]">{children}</main>
    </div>
  );
};

